import type { HTMLAttributes } from 'react';
import styled from 'styled-components';
import {
    ColorNeutralWhite,
    Spacing16,
    Spacing24,
    Spacing32,
    Spacing40,
    Spacing56,
    Spacing72
} from 'autogen/design-tokens/tokens';
import { breakpoint, sizes } from 'variables';
import { customScrollbar } from 'modules/helpers/mixins';
import imageOverlayStyle from 'components/shared/style/imageOverlay';
import Image from 'components/generic/Image';
import { ProductNameCompact } from 'components/generic/productName/ProductName';
import { Typography } from 'components/generic/typography';
import Header from './components/Headline';
import Search from './components/Search';
import type { IQuickViewSearch } from './shared/types';

// #region Styles
const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${Spacing32};
    min-height: 0;
    flex: 0 1 100%;
`;

const Container = styled.div`
    width: 100%;
    padding: 0 ${Spacing24} ${Spacing24};
    overflow: auto;
    ${customScrollbar};

    ${breakpoint.up(sizes.md)} {
        width: 480px;
    }
`;

const TopContainer = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    padding-top: ${Spacing72};
    padding-bottom: ${Spacing56};
    gap: ${Spacing24};

    &:after {
        content: '';

        ${imageOverlayStyle}
    }
`;

const ProductImage = styled(Image.WithScaling)`
    align-self: center;
    max-width: 136px;
    max-height: 136px;

    ${breakpoint.up(sizes.lg)} {
        max-width: 206px;
        max-height: 206px;
    }
`;

const StyledProductNameCompact = styled(ProductNameCompact)`
    padding-bottom: ${Spacing16};
    position: sticky;
    top: 0;
    background-color: ${ColorNeutralWhite};
    z-index: 1;
`;

const SubHeadline = styled(Typography.Body)`
    margin-bottom: ${Spacing16};
`;

// #endregion Styles

export default function QuickViewSearch(props: IQuickViewSearch & HTMLAttributes<HTMLDivElement>) {
    const {
        translations: { headline, subHeadline, search },
        inputType,
        searchQuery,
        trackingCategory,
        children,
        inputRef,
        productInfo,
        ...restProps
    } = props;

    return (
        <Wrapper>
            {productInfo?.image && (
                <TopContainer>
                    <ProductImage
                        src={productInfo.image}
                        sizes={[[sizes.xl, 206], 136]}
                        transformType="WidthScale"
                        imageType="product"
                        alt=""
                    />
                </TopContainer>
            )}
            <Container {...restProps}>
                <Header headline={headline} />
                {productInfo?.productName && (
                    <StyledProductNameCompact productName={productInfo.productName} size="md" />
                )}
                {subHeadline && <SubHeadline>{subHeadline}</SubHeadline>}
                <Search
                    translations={search}
                    inputType={inputType}
                    searchQuery={searchQuery}
                    inputRef={inputRef}
                    trackingCategory={trackingCategory}
                />
                {children}
            </Container>
        </Wrapper>
    );
}
